import { useEffect, useState } from 'react';
import { TimelineActivity } from '../../types/accounts';
import { apiClient } from '../../lib/api';
import { formatRelativeTime } from '../../utils/dateUtils';

interface Props {
  entityId: string;
  /** Override the data source (SKY-478: the portal passes its client-safe
   * /portal/accounts timeline — the default operator endpoint 403s for client
   * JWTs). Must be referentially stable across renders. */
  fetchActivities?: (entityId: string) => Promise<TimelineActivity[]>;
}

const fetchOperatorActivities = (entityId: string): Promise<TimelineActivity[]> =>
  apiClient.getEntityTimeline(entityId, 15).then((res) => res.activities ?? []);

export function ClientTimeline({ entityId, fetchActivities = fetchOperatorActivities }: Props) {
  const [activities, setActivities] = useState<TimelineActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setFetchError(false);
    setActivities([]);
    fetchActivities(entityId)
      .then((items) => { if (!stale) setActivities(items); })
      .catch(() => { if (!stale) setFetchError(true); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [entityId, fetchActivities]);

  return (
    <div className="mb-4 border border-neutral-200 rounded-md overflow-hidden">
      {/* Header / toggle */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls="client-timeline-content"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-neutral-50 hover:bg-neutral-100 transition"
      >
        <span className="text-13 font-semibold text-neutral-700">
          Activity Timeline
          {!loading && activities.length > 0 && (
            <span className="ml-1.5 text-xs font-normal text-neutral-400">
              ({activities.length})
            </span>
          )}
        </span>
        <span className="text-neutral-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div id="client-timeline-content" className="px-3 py-2 bg-white">
          {loading ? (
            <p className="text-xs text-neutral-400 italic py-1">Loading activity…</p>
          ) : fetchError ? (
            <p className="text-xs text-neutral-400 italic py-1">Activity unavailable.</p>
          ) : activities.length === 0 ? (
            <p className="text-xs text-neutral-400 italic py-1">No recent activity.</p>
          ) : (
            <ul className="space-y-1.5 py-1">
              {activities.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {a.source_type && (
                      <span className="text-2xs font-medium text-neutral-500 bg-neutral-100 rounded px-1 py-0.5 shrink-0">
                        {a.source_type}
                      </span>
                    )}
                    <span className="text-xs text-neutral-700 truncate">{a.label ?? a.activity_type}</span>
                  </div>
                  <span className="text-2xs text-neutral-400 whitespace-nowrap shrink-0">
                    {formatRelativeTime(a.occurred_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
